import { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "../../components/common/PageHeader";
import api from "../../utils/api";
import { Country } from "country-state-city";
import { getDefaultLocaleForCountry, getDefaultTimezoneForCountry } from "./utils/regionUtils";
import RegionSection from "./components/RegionSection";
import LanguagesSection from "./components/LanguagesSection";
import PrintCategoriesSection from "./components/PrintCategoriesSection";
import ResetSuperAdminPasswordSection from "./components/ResetSuperAdminPasswordSection";
import AmcManagementSection from "./components/AmcManagementSection";
import FeaturesSection from "./components/FeaturesSection";

const capitalizeFirst = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const Configure = () => {
  const role = localStorage.getItem("role");

  const [countries, setCountries] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [regionLocale, setRegionLocale] = useState("en-IN");
  const [regionTimezone, setRegionTimezone] = useState("Asia/Kolkata");
  const [isSavingRegion, setIsSavingRegion] = useState(false);

  const [languages, setLanguages] = useState([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [newLang, setNewLang] = useState({ code: "", name: "", direction: "ltr" });
  const [langErrors, setLangErrors] = useState({});

  const [printCategories, setPrintCategories] = useState([]);
  const [isLoadingPrintCategories, setIsLoadingPrintCategories] = useState(false);
  const [newPrintCategory, setNewPrintCategory] = useState({ name: "", key: "" });
  const [printCatErrors, setPrintCatErrors] = useState({});

  const [features, setFeatures] = useState({
    takeaway: true,
    onlineOrders: true,
    tableBooking: true,
    feedback: true,
    qrFeature: true,
  });
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  const [savingFeatureKey, setSavingFeatureKey] = useState("");

  useEffect(() => {
    if (role && role !== "fingliderAdmin") {
      toast.error("You don't have access to Configure");
    }
  }, [role]);

  useEffect(() => {
    try {
      const all = Country.getAllCountries() || [];
      setCountries(all);
    } catch (err) {
      console.error("Failed to load countries from country-state-city", err);
      setCountries([]);
    }
  }, []);

  const loadRegion = async () => {
    try {
      const res = await api.get("/settings/region");
      if (res?.data?.success) {
        const country = res.data.country || "";
        const locale = res.data.locale || getDefaultLocaleForCountry(country || "IN");
        const timezone = res.data.timezone || getDefaultTimezoneForCountry(country || "IN");
        setSelectedCountryCode(country || "IN");
        setRegionLocale(locale);
        setRegionTimezone(timezone);
      }
    } catch (err) {
      console.error("Failed to load region", err);
    }
  };

  const loadLanguages = async () => {
    setIsLoadingLanguages(true);
    try {
      const res = await api.get("/languages/all");
      setLanguages(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load languages");
    } finally {
      setIsLoadingLanguages(false);
    }
  };

  const loadPrintCategories = async () => {
    setIsLoadingPrintCategories(true);
    try {
      const res = await api.get("/restaurant/printCategories/all");
      setPrintCategories(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load print categories");
    } finally {
      setIsLoadingPrintCategories(false);
    }
  };

  const loadFeatures = async () => {
    setIsLoadingFeatures(true);
    try {
      const res = await api.get("/settings/features");
      if (res?.data?.success) {
        setFeatures((p) => ({
          ...p,
          ...(res.data.features || {}),
        }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load features");
    } finally {
      setIsLoadingFeatures(false);
    }
  };

  useEffect(() => {
    loadRegion();
    loadLanguages();
    loadPrintCategories();
    loadFeatures();
  }, []);

  const handleCountryChange = (code) => {
    setSelectedCountryCode(code);
    setRegionLocale(getDefaultLocaleForCountry(code));
    setRegionTimezone(getDefaultTimezoneForCountry(code));
  };

  const saveRegion = async () => {
    if (isSavingRegion) return;
    setIsSavingRegion(true);
    try {
      const payload = {
        country: selectedCountryCode || "IN",
        locale: regionLocale,
        timezone: regionTimezone,
      };
      await api.post("/settings/region", payload);
      toast.success("Region updated");
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update region");
    } finally {
      setIsSavingRegion(false);
    }
  };

  const handleNewLangChange = (field, value) => {
    setNewLang((p) => ({ ...p, [field]: value }));
    if (langErrors[field]) setLangErrors((p) => ({ ...p, [field]: "" }));
  };

  const addLanguage = async () => {
    const code = (newLang.code || "").trim().toLowerCase();
    const name = capitalizeFirst((newLang.name || "").trim());
    const errors = {};
    if (!code) errors.code = "Code is required";
    if (!name) errors.name = "Name is required";
    if (Object.keys(errors).length > 0) {
      setLangErrors(errors);
      return;
    }
    try {
      const res = await api.post("/languages", {
        code,
        name,
        direction: newLang.direction || "ltr",
      });
      toast.success(res?.data?.reactivated ? "Language re-enabled" : "Language added");
      setTimeout(() => window.location.reload(), 2000);
      setNewLang({ code: "", name: "", direction: "ltr" });
      setLangErrors({});
      await loadLanguages();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add language");
    }
  };

  const toggleLanguage = async (code) => {
    try {
      await api.patch(`/languages/${encodeURIComponent(code)}/toggle`);
      await loadLanguages();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update language");
    }
  };

  const handleNewPrintCategoryChange = (field, value) => {
    setNewPrintCategory((p) => ({ ...p, [field]: value }));
    if (printCatErrors[field]) setPrintCatErrors((p) => ({ ...p, [field]: "" }));
  };

  const addPrintCategory = async () => {
    const name = capitalizeFirst((newPrintCategory.name || "").trim());
    const key = (newPrintCategory.key || "").trim();
    const errors = {};
    if (!name) errors.name = "Name is required";
    if (!key) errors.key = "Key is required";
    if (Object.keys(errors).length > 0) {
      setPrintCatErrors(errors);
      return;
    }
    try {
      const res = await api.post("/restaurant/printCategories", { name, key });
      toast.success(res?.data?.reactivated ? "Print category re-enabled" : "Print category added");
      setTimeout(() => window.location.reload(), 2000);
      setNewPrintCategory({ name: "", key: "" });
      setPrintCatErrors({});
      await loadPrintCategories();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add print category");
    }
  };

  const togglePrintCategory = async (id) => {
    try {
      await api.patch(`/restaurant/printCategories/${id}/toggle`);
      await loadPrintCategories();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update print category");
    }
  };

  const toggleFeature = async (key, nextValue) => {
    if (!key || savingFeatureKey) return;

    const prev = features?.[key];
    setSavingFeatureKey(key);
    setFeatures((p) => ({ ...p, [key]: !!nextValue }));

    try {
      const res = await api.patch("/settings/features", { [key]: !!nextValue });
      if (res?.data?.success && res?.data?.features) {
        setFeatures((p) => ({ ...p, ...(res.data.features || {}) }));
        toast.success("Status changed successfully");
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (err) {
      setFeatures((p) => ({ ...p, [key]: prev }));
      toast.error(err?.response?.data?.message || "Failed to update feature");
    } finally {
      setSavingFeatureKey("");
    }
  };

  return (
    <div>
      <PageHeader
        showBackButton={true}
        backTo={-1}
        title="Configure"
        subtitle="Region, languages, print categories, and features"
      />
      <div className="mt-6 space-y-6 pb-12">
        <RegionSection
          countries={countries}
          selectedCountryCode={selectedCountryCode}
          regionLocale={regionLocale}
          regionTimezone={regionTimezone}
          isSaving={isSavingRegion}
          onCountryChange={handleCountryChange}
          onSave={saveRegion}
        />
        <LanguagesSection
          languages={languages}
          isLoading={isLoadingLanguages}
          newLang={newLang}
          langErrors={langErrors}
          onNewLangChange={handleNewLangChange}
          onAddLanguage={addLanguage}
          onToggleLanguage={toggleLanguage}
        />
        <PrintCategoriesSection
          printCategories={printCategories}
          isLoading={isLoadingPrintCategories}
          newPrintCategory={newPrintCategory}
          printCatErrors={printCatErrors}
          onNewPrintCategoryChange={handleNewPrintCategoryChange}
          onAddPrintCategory={addPrintCategory}
          onTogglePrintCategory={togglePrintCategory}
        />
        <FeaturesSection
          features={features}
          isLoading={isLoadingFeatures}
          isSavingKey={savingFeatureKey}
          onToggle={toggleFeature}
        />
        <AmcManagementSection />
        {role === "fingliderAdmin" ? <ResetSuperAdminPasswordSection /> : null}
      </div>
      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default Configure;
